import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { CalendarTodayOutlined } from '@mui/icons-material';
import { getScrollParent, isScrollbarClick } from '@practium/ui';
import { MONTH_ABBR_PT, MONTH_NAMES_PT } from '../../constants/maintenance.constants';
import styles from './MonthPicker.module.css';

export interface MonthPickerProps {
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
	hasError?: boolean;
	disabled?: boolean;
	allowedMonths?: string[];
	showYear?: boolean;
	triggerClassName?: string;
}

export const MonthPicker = ({
	value,
	onChange,
	placeholder = 'Ex.: Janeiro',
	hasError,
	disabled,
	allowedMonths,
	showYear,
	triggerClassName,
}: MonthPickerProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const [year, setYear] = useState(() => {
		if (value) return Number(value.split('-')[0]);
		return new Date().getFullYear();
	});
	const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
	const wrapperRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLDivElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);

	const displayValue = value
		? `${MONTH_NAMES_PT[value.split('-')[1]] ?? value}${showYear ? ` ${value.split('-')[0]}` : ''}`
		: '';

	const updatePosition = useCallback(() => {
		if (!triggerRef.current) return;
		const rect = triggerRef.current.getBoundingClientRect();
		const dropdownWidth = Math.max(dropdownRef.current?.offsetWidth ?? 0, rect.width, 208);
		const maxLeft = window.innerWidth - dropdownWidth - 8;
		setDropdownStyle({
			top: rect.bottom + 4,
			left: Math.max(8, Math.min(rect.left, maxLeft)),
			minWidth: rect.width,
		});
	}, []);

	useEffect(() => {
		if (!allowedMonths?.length) return;
		const years = [
			...new Set(
				allowedMonths
					.map((month) => Number(month.replace(/\//g, '-').slice(0, 4)))
					.filter(Number.isFinite)
			),
		];
		if (years.length && !years.includes(year)) setYear(years[0]);
	}, [allowedMonths, year]);

	useEffect(() => {
		if (!isOpen) return;

		updatePosition();
		const scrollParent = getScrollParent(wrapperRef.current);
		const previousPadding = scrollParent.style.paddingBottom;

		const syncScrollRoom = () => {
			updatePosition();
			requestAnimationFrame(() => {
				updatePosition();
				const dropdownHeight = dropdownRef.current?.offsetHeight ?? 224;
				scrollParent.style.paddingBottom = `${dropdownHeight + 16}px`;
			});
		};

		syncScrollRoom();

		const handleOutsideClick = (event: MouseEvent) => {
			const target = event.target as Node;
			if (wrapperRef.current?.contains(target) || dropdownRef.current?.contains(target)) return;
			if (isScrollbarClick(event)) return;
			setIsOpen(false);
		};

		document.addEventListener('mousedown', handleOutsideClick);
		document.addEventListener('scroll', updatePosition, true);
		window.addEventListener('resize', updatePosition);

		return () => {
			scrollParent.style.paddingBottom = previousPadding;
			document.removeEventListener('mousedown', handleOutsideClick);
			document.removeEventListener('scroll', updatePosition, true);
			window.removeEventListener('resize', updatePosition);
		};
	}, [isOpen, updatePosition]);

	const allowedSet = allowedMonths
		? new Set(allowedMonths.map((month) => month.replace(/\//g, '-').slice(0, 7)))
		: null;
	const allowedYears = allowedSet
		? [...new Set([...allowedSet].map((month) => Number(month.slice(0, 4))).filter(Number.isFinite))]
		: [];
	const canGoPrevYear =
		!allowedSet || allowedYears.some((allowedYear) => allowedYear < year);
	const canGoNextYear =
		!allowedSet || allowedYears.some((allowedYear) => allowedYear > year);

	const handleMonthSelect = (monthIndex: number) => {
		const mm = String(monthIndex + 1).padStart(2, '0');
		const next = `${year}-${mm}`;
		if (allowedSet && !allowedSet.has(next)) return;
		onChange(next);
		setIsOpen(false);
	};

	const selectedMonth = value ? Number(value.split('-')[1]) - 1 : -1;
	const selectedYear = value ? Number(value.split('-')[0]) : -1;

	const dropdown = isOpen ? (
		<div
			ref={dropdownRef}
			className={styles.dropdown}
			style={dropdownStyle}
			onMouseDown={(event) => event.stopPropagation()}
			onWheel={(event) => event.stopPropagation()}
		>
			<div className={styles.yearNav}>
				<button
					type="button"
					className={styles.yearBtn}
					disabled={!canGoPrevYear}
					onClick={() => canGoPrevYear && setYear((y) => y - 1)}
				>
					‹
				</button>
				<span className={styles.yearLabel}>{year}</span>
				<button
					type="button"
					className={styles.yearBtn}
					disabled={!canGoNextYear}
					onClick={() => canGoNextYear && setYear((y) => y + 1)}
				>
					›
				</button>
			</div>
			<div className={styles.monthGrid}>
				{MONTH_ABBR_PT.map((abbr, idx) => {
					const monthValue = `${year}-${String(idx + 1).padStart(2, '0')}`;
					const isAllowed = !allowedSet || allowedSet.has(monthValue);
					return (
						<button
							key={abbr}
							type="button"
							disabled={!isAllowed}
							className={`${styles.monthBtn} ${idx === selectedMonth && year === selectedYear ? styles.monthBtnSelected : ''} ${!isAllowed ? styles.monthBtnDisabled : ''}`}
							onMouseDown={() => isAllowed && handleMonthSelect(idx)}
						>
							{abbr}
						</button>
					);
				})}
			</div>
		</div>
	) : null;

	return (
		<div ref={wrapperRef} className={styles.wrapper}>
			<div
				ref={triggerRef}
				className={`${styles.trigger} ${hasError ? styles.error : ''} ${isOpen ? styles.open : ''} ${!value ? styles.empty : ''} ${disabled ? styles.disabled : ''} ${triggerClassName ?? ''}`}
				onClick={() => !disabled && setIsOpen((o) => !o)}
				aria-disabled={disabled}
			>
				<span className={`${styles.triggerText} ${displayValue ? '' : styles.placeholder}`}>
					{displayValue || placeholder}
				</span>
				<CalendarTodayOutlined className={styles.icon} />
			</div>
			{typeof document !== 'undefined' && createPortal(dropdown, document.body)}
		</div>
	);
};
